import { API_BASE_URL } from '../config/apiConfig';

interface ExchangeRates {
  [key: string]: number;
}

interface CurrencyCache {
  rates: ExchangeRates;
  timestamp: number;
}

class CurrencyService {
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour
  private cache: CurrencyCache | null = null;
  private fetchPromise: Promise<ExchangeRates> | null = null;

  /**
   * Fetch exchange rates from our backend
   */
  private async fetchExchangeRates(): Promise<ExchangeRates> {
    if (this.fetchPromise) return this.fetchPromise;

    this.fetchPromise = (async () => {
      try {
        console.log('🌍 Fetching exchange rates from SightTrail API...');
        const response = await fetch(`${API_BASE_URL}/currency/rates`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`SightTrail API error: ${response.status}`);
        }

        const rawData = await response.json();
        // Backend returns { success: true, data: { USD: 1, KES: 128, ... } }
        const rates: ExchangeRates = rawData.data || rawData;

        this.cache = {
          rates,
          timestamp: Date.now(),
        };

        // Persist for session
        try {
          sessionStorage.setItem('exchange_rates_cache', JSON.stringify(this.cache));
        } catch (e) {}

        return rates;
      } catch (error) {
        console.error('❌ Error fetching exchange rates:', error);

        // Try session storage fallback
        try {
          const cached = sessionStorage.getItem('exchange_rates_cache');
          if (cached) {
            const cachedData = JSON.parse(cached);
            this.cache = cachedData;
            return cachedData.rates;
          }
        } catch (e) {}

        return this.getDefaultRates();
      } finally {
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  private getDefaultRates(): ExchangeRates {
    return {
      USD: 1.0,
      KES: 129.5,
      EUR: 0.92,
      GBP: 0.79,
    };
  }

  /**
   * Get exchange rates
   */
  async getExchangeRates(): Promise<ExchangeRates> {
    if (this.cache && (Date.now() - this.cache.timestamp) < this.CACHE_DURATION) {
      return this.cache.rates;
    }

    try {
      const cached = sessionStorage.getItem('exchange_rates_cache');
      if (cached) {
        const cachedData = JSON.parse(cached);
        if ((Date.now() - cachedData.timestamp) < this.CACHE_DURATION) {
          this.cache = cachedData;
          return cachedData.rates;
        }
      }
    } catch (e) {}

    return await this.fetchExchangeRates();
  }

  /**
   * Convert amount
   */
  async convert(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
    const from = fromCurrency?.toUpperCase() || 'USD';
    const to = toCurrency?.toUpperCase() || 'USD';

    if (from === to) return amount;

    const rates = await this.getExchangeRates();
    if (!rates[from] || !rates[to]) {
      console.warn(`Missing rate for ${from} or ${to}. Using base USD conversion.`);
      if (from === 'USD' && rates[to]) return amount * rates[to];
      if (to === 'USD' && rates[from]) return amount / rates[from];
      return amount;
    }

    // Convert from -> USD -> to
    const inUsd = from === 'USD' ? amount : amount / rates[from];
    const final = to === 'USD' ? inUsd : inUsd * rates[to];

    return Math.ceil(final); // Round up for safety in pricing
  }

  /**
   * Get rates synchronously from cache
   */
  getRatesSync(): ExchangeRates | null {
    if (this.cache && (Date.now() - this.cache.timestamp) < this.CACHE_DURATION) {
      return this.cache.rates;
    }
    return null;
  }

  /**
   * Convert amount synchronously
   */
  convertSync(amount: number, fromCurrency: string, toCurrency: string): number | null {
    const from = fromCurrency?.toUpperCase() || 'USD';
    const to = toCurrency?.toUpperCase() || 'USD';

    if (from === to) return amount;

    const rates = this.getRatesSync();
    if (!rates || !rates[from] || !rates[to]) {
      return null;
    }

    const inUsd = from === 'USD' ? amount : amount / rates[from];
    const final = to === 'USD' ? inUsd : inUsd * rates[to];

    return Math.ceil(final);
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number, currency: string): string {
    const code = currency?.toUpperCase() || 'USD';
    const symbols: { [key: string]: string } = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      KES: 'KES ',
    };

    const symbol = symbols[code] || `${code} `;
    
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `${symbol}0`;
    }

    try {
      return `${symbol}${Math.ceil(amount).toLocaleString('en-US')}`;
    } catch (error) {
      return `${symbol}${amount}`;
    }
  }

  /**
   * Get all supported currencies dynamically from the rates object
   */
  async getSupportedCurrencies(): Promise<string[]> {
    const rates = await this.getExchangeRates();
    return Object.keys(rates).sort();
  }
}

export const currencyService = new CurrencyService();
export default currencyService;
